import React, { useState, useRef, useEffect } from 'react';
import { getAttendeeByQrCode, checkInAttendee } from '../../services/event.service';
import { EventAttendee } from '../../types/event';
import { useToast } from '../../hooks/useToast';
import { QrReader } from 'react-qr-reader';
import { checkTicketFraud, logTicketValidationAttempt } from '../../services/security.service';

interface QRCodeScannerProps {
  onSuccess?: (attendee: EventAttendee) => void;
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({ onSuccess }) => {
  const [scanning, setScanning] = useState<boolean>(false);
  const [qrCode, setQrCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [attendee, setAttendee] = useState<EventAttendee | null>(null);
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [scanResult, setScanResult] = useState<'success' | 'error' | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  
  useEffect(() => {
    if (scanning && inputRef.current) {
      inputRef.current.focus();
    }
  }, [scanning]);
  
  const handleStartScan = () => {
    setScanning(true);
    setQrCode('');
    setError(null);
    setAttendee(null);
    setScanResult(null);
  };
  
  const handleStopScan = () => {
    setScanning(false);
    setCameraActive(false);
  };
  
  const handleStartCamera = () => {
    setScanning(true);
    setCameraActive(true);
    setScanResult(null);
    setError(null);
    setAttendee(null);
    setQrCode('');
  };
  
  const handleStopCamera = () => {
    setCameraActive(false);
    setScanning(false);
    setScanResult(null);
    setQrCode('');
    setError(null);
    setAttendee(null);
  };
  
  const handleCameraScan = async (result: any) => {
    if (result && !loading) {
      // Extraire le code QR du résultat du scan
      const scannedCode = result?.text;
      if (scannedCode) {
        setQrCode(scannedCode);
        const success = await processQrCode(scannedCode);
        
        // Si le scan est réussi, fermer la caméra et afficher les infos du ticket
        if (success) {
          setCameraActive(false);
        }
      }
    }
  };
  
  const handleCameraError = (error: any) => {
    console.error('Erreur de caméra:', error);
    setError('Impossible d\'accéder à la caméra. Veuillez vérifier les permissions.');
    setCameraActive(false);
    showToast('Erreur d\'accès à la caméra', 'error');
  };
  
  const handleQrCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQrCode(e.target.value);
  };
  
  const processQrCode = async (code: string): Promise<boolean> => {
    if (!code.trim()) {
      setError('Code QR invalide');
      setScanResult('error');
      return false;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Vérification anti-fraude
      const deviceInfo = {
        userAgent: navigator.userAgent,
        location: window.location.href
      };
      
      const fraudCheck = await checkTicketFraud(code);
      
      // Enregistrer la tentative de validation
      await logTicketValidationAttempt(code, fraudCheck.valid, deviceInfo);
      
      if (!fraudCheck.valid) {
        // Ticket invalide ou déjà utilisé
        if (fraudCheck.attendee) {
          // On affiche quand même les informations du ticket
          setAttendee(fraudCheck.attendee);
          setScanResult('error');
          
          if (fraudCheck.fraudDetected) {
            // Alerte de fraude potentielle
            setError(`ALERTE DE SÉCURITÉ : ${fraudCheck.reason} - Tentatives multiples détectées`);
            showToast('Alerte de sécurité : Tentative de fraude détectée', 'error');
          } else {
            setError(fraudCheck.reason || 'Ticket invalide');
            showToast(fraudCheck.reason || 'Ticket invalide', 'error');
          }
          
          if (onSuccess) {
            onSuccess(fraudCheck.attendee);
          }
          
          return true; // On retourne true pour afficher les infos du ticket
        } else {
          setError(fraudCheck.reason || 'Ticket invalide ou introuvable');
          showToast(fraudCheck.reason || 'Ticket invalide ou introuvable', 'error');
          setScanResult('error');
          return false;
        }
      }
      
      // Le ticket est valide
      setAttendee(fraudCheck.attendee!);
      setScanResult('success');
      
      if (onSuccess) {
        onSuccess(fraudCheck.attendee!);
      }
      
      // Auto check-in pour une expérience fluide
      await handleCheckIn(fraudCheck.attendee!);
      return true;
      
    } catch (error: any) {
      setError(error.message || 'QR code invalide ou introuvable');
      showToast('QR code invalide ou introuvable', 'error');
      setScanResult('error');
      return false;
    } finally {
      setLoading(false);
      // Ne pas fermer la caméra pour permettre des scans consécutifs
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await processQrCode(qrCode);
    return success;
  };
  
  const handleCheckIn = async (attendeeData: EventAttendee | null = null) => {
    const targetAttendee = attendeeData || attendee;
    if (!targetAttendee) return;
    
    try {
      setLoading(true);
      const updatedAttendee = await checkInAttendee(targetAttendee.id);
      setAttendee(updatedAttendee);
      showToast('Participant enregistré avec succès', 'success');
      
      // Reset after successful check-in
      setTimeout(() => {
        setQrCode('');
        setAttendee(null);
        setScanResult(null);
        // Ne pas fermer la caméra pour permettre des scans consécutifs
        if (!cameraActive && inputRef.current) {
          inputRef.current.focus();
        }
      }, 2000);
      
    } catch (error: any) {
      showToast(error.message || 'Erreur lors de l\'enregistrement du participant', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {!scanning ? (
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"></path>
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Scanner un QR code</h3>
          <p className="mt-1 text-sm text-gray-500">
            Commencez à scanner les QR codes des participants pour vérifier leur ticket.
          </p>
          <div className="mt-6 flex flex-col space-y-3">
            <button
              onClick={handleStartCamera}
              className="inline-flex items-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              Scanner avec la caméra
            </button>
            <button
              onClick={handleStartScan}
              className="inline-flex items-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
              Saisir manuellement
            </button>
          </div>
        </div>
      ) : cameraActive ? (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Scanner avec la caméra</h3>
            <button
              onClick={handleStopCamera}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {scanResult === 'success' && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <p className="text-green-700 text-sm font-medium">Ticket validé avec succès</p>
              </div>
            </div>
          )}

          {scanResult === 'error' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                <p className="text-red-700 text-sm font-medium">{error || 'Ticket invalide'}</p>
              </div>
            </div>
          )}

          <div className="mb-4 overflow-hidden rounded-lg border border-gray-300">
            <QrReader
              constraints={{ facingMode: 'environment' }}
              onResult={handleCameraScan}
              containerStyle={{ width: '100%', height: '100%' }}
              videoStyle={{ width: '100%', height: '100%' }}
              videoContainerStyle={{ width: '100%', height: '100%', paddingTop: '100%' }}
            />
          </div>

          <p className="text-sm text-gray-500 text-center mb-4">
            Positionnez le QR code du ticket dans le cadre pour le scanner automatiquement
          </p>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">Saisie manuelle</h3>
            <button
              onClick={handleStopScan}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="mb-4">
              <label htmlFor="qrCode" className="block text-sm font-medium text-gray-700 mb-1">
                Code QR
              </label>
              <div className="flex">
                <input
                  ref={inputRef}
                  type="text"
                  id="qrCode"
                  value={qrCode}
                  onChange={handleQrCodeChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Saisissez le code QR"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-r-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                  )}
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>
          </form>
          
          {attendee && (
            <div className="border rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">Informations du ticket</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  attendee.status === 'checked_in' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {attendee.status === 'checked_in' ? 'Déjà enregistré' : 'En attente'}
                </span>
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Événement</p>
                  <p className="text-sm text-gray-900">{attendee.event?.title}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Participant</p>
                  <p className="text-sm text-gray-900">
                    {attendee.user?.name || attendee.attendee_name || 'Anonyme'}
                    {(attendee.user?.email || attendee.attendee_email) && ` (${attendee.user?.email || attendee.attendee_email})`}
                    {attendee.ticket_order?.phone || attendee.attendee_phone ? ` - ${attendee.ticket_order?.phone || attendee.attendee_phone}` : ''}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Nom du ticket</p>
                  <p className="text-sm text-gray-900">
                    {attendee.ticket_order?.ticket?.name || 'N/A'}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Type et prix du ticket</p>
                  <p className="text-sm text-gray-900">
                    {attendee.ticket_order?.ticket?.ticket_type ? 
                      attendee.ticket_order.ticket.ticket_type === 'standard' ? 'Standard' :
                      attendee.ticket_order.ticket.ticket_type === 'vip' ? 'VIP' :
                      attendee.ticket_order.ticket.ticket_type === 'free' ? 'Gratuit' :
                      attendee.ticket_order.ticket.ticket_type === 'early_bird' ? 'Early Bird' : 'N/A'
                    : 'N/A'}
                    {attendee.ticket_order?.amount !== undefined && ` - ${attendee.ticket_order.amount.toLocaleString()} FCFA`}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-500">Code QR</p>
                  <p className="text-sm text-gray-900">{attendee.qr_code}</p>
                </div>
              </div>
              
              {attendee.status !== 'checked_in' && (
                <button
                  onClick={() => handleCheckIn()}
                  disabled={loading}
                  className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Enregistrer le participant
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          
          <p className="text-xs text-gray-500 text-center">
            Scannez le code QR du participant ou saisissez-le manuellement pour vérifier le ticket.
          </p>
        </div>
      )}
    </div>
  );
};

export default QRCodeScanner;
